import type { TAssignReviewTeacher, TError, TSpaceParticipantCandidatesResponse } from '@onesource/schemas';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import axios from '../../axios';
import { getReviewQueryKey } from './use-review-queue';

/** Admin: the program's teachers for the assign-teacher picker, searched on the server. */
export const useGetReviewTeachers = (spaceId: string, search: string, enabled = true) =>
  useQuery<TSpaceParticipantCandidatesResponse, TError>({
    queryKey: [...getReviewQueryKey(spaceId), 'teachers', search],
    queryFn: async () => {
      const res = await axios.get<TSpaceParticipantCandidatesResponse>(
        `/community-hub/spaces/${spaceId}/review/teachers`,
        {
          params: { search: search || undefined },
        },
      );
      return res.data;
    },
    enabled: !!spaceId && enabled,
    placeholderData: keepPreviousData,
  });

/** Admin: give the student of a submission with no teacher a teacher. */
export const useAssignReviewTeacher = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, TError, TAssignReviewTeacher & { spaceId: string; submissionId: string }>({
    mutationFn: async ({ spaceId, submissionId, teacherUserUuid }) => {
      const res = await axios.put(`/community-hub/spaces/${spaceId}/review/submissions/${submissionId}/teacher`, {
        teacherUserUuid,
      });
      return res.data;
    },
    onSuccess: (_result, { spaceId }) => {
      queryClient.invalidateQueries({ queryKey: getReviewQueryKey(spaceId) });
    },
  });
};
